// Local file-system access for the uploader Explorer, behind one interface that works
// with the File System Access API (Chromium — lazy + resumable) and degrades to
// <input webkitdirectory> / drag-drop (Safari/Firefox — snapshot, no cross-session
// resume). Structural handle types avoid depending on a particular TS lib.dom version.
import { useCallback, useMemo, useRef, useState } from "react";
import { supportsFileSystemAccess, requestPersistentStorage } from "./fsCapability";
import { saveJobHandle, getJobHandle } from "./localManifest";

export interface FileHandle {
  kind: "file";
  name: string;
  getFile(): Promise<File>;
}
/** Minimal writable-file shape (File System Access) for saving downloads locally. */
export interface WritableFileHandle {
  createWritable(): Promise<{
    write(data: Blob | BufferSource): Promise<void>;
    close(): Promise<void>;
    abort?(): Promise<void>;
  }>;
}
export interface DirHandle {
  kind: "directory";
  name: string;
  values(): AsyncIterableIterator<DirHandle | FileHandle>;
  queryPermission?(d: { mode: "read" | "readwrite" }): Promise<PermissionState>;
  requestPermission?(d: { mode: "read" | "readwrite" }): Promise<PermissionState>;
  /** Present only on real File System Access handles (Chromium) — used to write
   *  downloaded files into the folder the user has open in the local pane. */
  getFileHandle?(name: string, opts?: { create?: boolean }): Promise<WritableFileHandle>;
}

/** Ensure readwrite permission on `dir` (File System Access, Chromium only). Call it
 *  directly from the click handler, before any network await: a permission prompt
 *  needs transient user activation, which expires a few seconds after the click.
 *  Returns false when unsupported or denied. Never throws. */
export const ensureWritable = async (dir: DirHandle | null | undefined): Promise<boolean> => {
  if (!dir?.getFileHandle) return false;
  try {
    if (dir.queryPermission && (await dir.queryPermission({ mode: "readwrite" })) === "granted") return true;
    if (dir.requestPermission) return (await dir.requestPermission({ mode: "readwrite" })) === "granted";
    return true;
  } catch {
    return false;
  }
};

/** `name` if it's free in `dir`, else "name (1).ext", "name (2).ext", … */
const freeName = async (dir: DirHandle, name: string): Promise<string> => {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  for (let i = 0; i < 1000; i += 1) {
    const candidate = i === 0 ? name : `${stem} (${i})${ext}`;
    try {
      await dir.getFileHandle!(candidate); // exists → try the next suffix
    } catch (e) {
      if ((e as DOMException)?.name === "NotFoundError") return candidate;
      if ((e as DOMException)?.name === "TypeMismatchError") continue; // a folder has that name
      throw e;
    }
  }
  throw new Error("no free file name");
};

/** Write `data` into `dir` as `name` (File System Access, Chromium only), never
 *  overwriting an existing file — a clash gets a " (n)" suffix like a browser download.
 *  Expects readwrite permission to have been granted already (see `ensureWritable`).
 *  Returns false when unsupported or denied so callers can fall back to a normal
 *  browser download. Best-effort — never throws. */
export const saveToDir = async (dir: DirHandle | null | undefined, name: string, data: Blob): Promise<boolean> => {
  if (!dir?.getFileHandle) return false;
  try {
    if (dir.queryPermission && (await dir.queryPermission({ mode: "readwrite" })) !== "granted") return false;
    const fh = await dir.getFileHandle(await freeName(dir, name), { create: true });
    const w = await fh.createWritable();
    try {
      await w.write(data);
      await w.close();
    } catch (e) {
      // Discard the swap file instead of leaving a partial download behind.
      await w.abort?.().catch(() => {});
      throw e;
    }
    return true;
  } catch {
    return false;
  }
};

/** A unified node the panes render — a folder or a file, from FSA or the fallback. */
export type FsNode =
  | { kind: "dir"; name: string; path: string; dir: DirHandle | null; children?: FsNode[] }
  | {
      kind: "file";
      name: string;
      path: string;
      size: number;
      lastModified: number;
      handle: FileHandle | null;
      file: File | null;
    };

const isImage = (name: string) => /\.(jpe?g|png|gif|webp|bmp|tiff?|heic|heif|avif)$/i.test(name);
const isVideo = (name: string) => /\.(mp4|mov|m4v|avi|mkv|webm|3gp)$/i.test(name);
export const isMedia = (name: string) => isImage(name) || isVideo(name);
export { isImage, isVideo };

const win = () =>
  window as unknown as {
    showDirectoryPicker?: (o?: { mode?: "read" | "readwrite" }) => Promise<DirHandle>;
  };

/** Resolve a `File` for a node regardless of source (fallback holds it; FSA fetches it).
 *  FSA nodes start with size/lastModified 0; the first fetch fills them in on the node
 *  so sorting and the resume manifest see real values. */
export const fileOf = async (node: Extract<FsNode, { kind: "file" }>): Promise<File | null> => {
  if (node.file) return node.file;
  if (!node.handle) return null;
  const f = await node.handle.getFile();
  node.size = f.size;
  node.lastModified = f.lastModified;
  return f;
};

/** Fill in size/lastModified for FSA nodes that haven't been read yet (lastModified 0
 *  means "not stat'ed" — real files always carry an mtime). Runs a few getFile() calls
 *  at a time; unreadable files are skipped. */
export const statFiles = async (nodes: FsNode[], concurrency = 16): Promise<void> => {
  const todo = nodes.filter(
    (n): n is Extract<FsNode, { kind: "file" }> => n.kind === "file" && !!n.handle && !n.file && n.lastModified === 0,
  );
  let i = 0;
  const worker = async () => {
    while (i < todo.length) {
      const n = todo[i++];
      await fileOf(n).catch(() => null);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, todo.length) }, worker));
};

// --- Fallback tree from a flat FileList (webkitRelativePath / drag-drop) ----------
const treeFromFiles = (files: File[], paths?: string[]): { root: FsNode; rootName: string } => {
  const relOf = (i: number) => paths?.[i] || files[i].webkitRelativePath || files[i].name;
  // Root the tree at the shared top folder when every file came from the same one
  // (a picked or dropped folder); otherwise (loose files, several dropped items) use a
  // synthetic root so nothing is misfiled under the first file's folder.
  const first = relOf(0).split("/");
  const shared =
    first.length > 1 && files.every((_, i) => { const segs = relOf(i).split("/"); return segs.length > 1 && segs[0] === first[0]; });
  const rootName = shared ? first[0] : "Selected files";
  type Dir = { kind: "dir"; name: string; path: string; dir: null; children: FsNode[] };
  const root: Dir = { kind: "dir", name: rootName, path: rootName, dir: null, children: [] };
  const dirs = new Map<string, Dir>([[rootName, root]]);
  const ensureDir = (parts: string[]): Dir => {
    let path = rootName;
    let cur = root;
    for (const part of parts) {
      path = `${path}/${part}`;
      let next = dirs.get(path);
      if (!next) {
        next = { kind: "dir", name: part, path, dir: null, children: [] };
        dirs.set(path, next);
        cur.children.push(next);
      }
      cur = next;
    }
    return cur;
  };
  files.forEach((f, i) => {
    const segs = relOf(i).split("/");
    const name = segs.pop() as string;
    // Drop the leading root segment (already `rootName`) when present.
    const parentSegs = shared ? segs.slice(1) : segs;
    const parent = ensureDir(parentSegs);
    parent.children.push({
      kind: "file",
      name,
      path: `${parent.path}/${name}`,
      size: f.size,
      lastModified: f.lastModified,
      handle: null,
      file: f,
    });
  });
  return { root, rootName };
};

// --- Drag-drop --------------------------------------------------------------------
// DataTransfer is emptied once the drop event returns, so everything is read from it
// synchronously up front; only then do the async parts (handles, directory walks) run.

type Entry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?(ok: (f: File) => void, err: (e: unknown) => void): void;
  createReader?(): { readEntries(ok: (e: Entry[]) => void, err: (e: unknown) => void): void };
};

/** Walk webkitGetAsEntry() entries (Safari/Firefox folder drops) into Files + paths. */
const walkEntries = async (entries: Entry[]): Promise<{ files: File[]; paths: string[] }> => {
  const files: File[] = [];
  const paths: string[] = [];
  const walk = async (e: Entry, prefix: string): Promise<void> => {
    const rel = prefix ? `${prefix}/${e.name}` : e.name;
    if (e.isFile && e.file) {
      const f = await new Promise<File | null>((res) => e.file!(res, () => res(null)));
      if (f) {
        files.push(f);
        paths.push(rel);
      }
    } else if (e.isDirectory && e.createReader) {
      const reader = e.createReader();
      // readEntries returns results in batches; keep reading until an empty batch.
      for (;;) {
        const batch = await new Promise<Entry[]>((res) => reader.readEntries(res, () => res([])));
        if (!batch.length) break;
        for (const c of batch) await walk(c, rel);
      }
    }
  };
  for (const e of entries) await walk(e, "");
  return { files, paths };
};

export interface LocalFs {
  supported: boolean;
  rootName: string | null;
  /** List the direct children of a folder node (lazy for FSA; precomputed for fallback). */
  readFolder: (node: Extract<FsNode, { kind: "dir" }> | null) => Promise<FsNode[]>;
  /** Open the OS folder picker (FSA) — returns the root node, or null if cancelled. */
  pickDirectory: () => Promise<Extract<FsNode, { kind: "dir" }> | null>;
  /** Fallback + drag-drop entry points that build the tree from plain Files. `paths`
   *  (optional, parallel to `files`) overrides each file's webkitRelativePath. */
  setFilesFallback: (files: File[], paths?: string[]) => Extract<FsNode, { kind: "dir" }> | null;
  /** Build a root from a drop. Must be called synchronously inside the drop handler
   *  (it snapshots the DataTransfer before its first await). A single dropped folder
   *  (FSA) comes back with its live handle, so it can be persisted like a picked one. */
  fromDrop: (dt: DataTransfer) => Promise<Extract<FsNode, { kind: "dir" }> | null>;
  /** Forget a folder's cached listing (FSA) so the next readFolder re-reads it from disk. */
  refresh: (node: Extract<FsNode, { kind: "dir" }>) => void;
  /** Walk the whole tree collecting media files (for search + upload). Unbounded —
   *  reads every subfolder (FSA listings are cached as they're read). */
  flatten: (node: Extract<FsNode, { kind: "dir" }>) => Promise<Extract<FsNode, { kind: "file" }>[]>;
  /** Persist the picked folder handle (FSA only) so a later session can re-open it. */
  persist: (key: string, node: Extract<FsNode, { kind: "dir" }>, galleryName?: string) => Promise<void>;
  /** Name of the previously-persisted folder for this key, or null. */
  savedFolderName: (key: string) => Promise<string | null>;
  /** Re-open the persisted folder (one permission click), or null if unavailable/denied. */
  resume: (key: string) => Promise<Extract<FsNode, { kind: "dir" }> | null>;
  reset: () => void;
}

export const useLocalFs = (): LocalFs => {
  const supported = useMemo(supportsFileSystemAccess, []);
  const [rootName, setRootName] = useState<string | null>(null);
  // Fallback keeps the whole tree in memory; FSA reads lazily from live handles.
  const fallbackRoot = useRef<Extract<FsNode, { kind: "dir" }> | null>(null);

  const readFolder = useCallback(
    async (node: Extract<FsNode, { kind: "dir" }> | null): Promise<FsNode[]> => {
      if (!node) return [];
      if (node.children) return node.children; // fallback (precomputed) or cached
      if (!node.dir) return [];
      const out: FsNode[] = [];
      for await (const entry of node.dir.values()) {
        if (entry.kind === "directory") {
          out.push({ kind: "dir", name: entry.name, path: `${node.path}/${entry.name}`, dir: entry });
        } else {
          // Defer the getFile() (size/mtime) until the tile/upload needs it.
          out.push({
            kind: "file",
            name: entry.name,
            path: `${node.path}/${entry.name}`,
            size: 0,
            lastModified: 0,
            handle: entry,
            file: null,
          });
        }
      }
      out.sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "dir" ? -1 : 1));
      node.children = out; // cache
      return out;
    },
    [],
  );

  const pickDirectory = useCallback(async (): Promise<Extract<FsNode, { kind: "dir" }> | null> => {
    const picker = win().showDirectoryPicker;
    if (!picker) return null;
    let handle: DirHandle;
    try {
      handle = await picker({ mode: "read" });
    } catch {
      return null; // user cancelled
    }
    setRootName(handle.name);
    return { kind: "dir", name: handle.name, path: handle.name, dir: handle };
  }, []);

  const setFilesFallback = useCallback((files: File[], paths?: string[]): Extract<FsNode, { kind: "dir" }> | null => {
    if (!files.length) return null;
    const { root, rootName: rn } = treeFromFiles(files, paths);
    fallbackRoot.current = root as Extract<FsNode, { kind: "dir" }>;
    setRootName(rn);
    return fallbackRoot.current;
  }, []);

  const fromDrop = useCallback(
    (dt: DataTransfer): Promise<Extract<FsNode, { kind: "dir" }> | null> => {
      // Synchronous snapshot — nothing below may touch `dt` after an await.
      const items = Array.from(dt.items || []).filter((it) => it.kind === "file");
      const looseFiles = Array.from(dt.files || []);
      type HandleItem = { getAsFileSystemHandle?: () => Promise<DirHandle | FileHandle | null> };
      const useHandles = supported && items.length > 0 && typeof (items[0] as unknown as HandleItem).getAsFileSystemHandle === "function";
      const handlePromises = useHandles
        ? items.map((it) => (it as unknown as Required<HandleItem>).getAsFileSystemHandle().catch(() => null))
        : [];
      const itemFiles = items.map((it) => it.getAsFile());
      const entries = useHandles
        ? []
        : items
            .map((it) => (it as unknown as { webkitGetAsEntry?: () => Entry | null }).webkitGetAsEntry?.() ?? null)
            .filter((e): e is Entry => !!e);

      return (async () => {
        if (useHandles) {
          const handles = await Promise.all(handlePromises);
          if (handles.length === 1 && handles[0]?.kind === "directory") {
            const h = handles[0];
            setRootName(h.name);
            return { kind: "dir", name: h.name, path: h.name, dir: h };
          }
          // Several items and/or loose files: a synthetic root over their handles.
          const rn = "Dropped items";
          const children: FsNode[] = [];
          handles.forEach((h, i) => {
            if (h?.kind === "directory") {
              children.push({ kind: "dir", name: h.name, path: `${rn}/${h.name}`, dir: h });
            } else if (h?.kind === "file") {
              children.push({ kind: "file", name: h.name, path: `${rn}/${h.name}`, size: 0, lastModified: 0, handle: h, file: null });
            } else if (itemFiles[i]) {
              const f = itemFiles[i]!;
              children.push({ kind: "file", name: f.name, path: `${rn}/${f.name}`, size: f.size, lastModified: f.lastModified, handle: null, file: f });
            }
          });
          if (!children.length) return null;
          children.sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "dir" ? -1 : 1));
          setRootName(rn);
          return { kind: "dir", name: rn, path: rn, dir: null, children };
        }
        if (entries.some((e) => e.isDirectory)) {
          const { files, paths } = await walkEntries(entries);
          return setFilesFallback(files, paths);
        }
        return setFilesFallback(looseFiles);
      })();
    },
    [supported, setFilesFallback],
  );

  const refresh = useCallback((node: Extract<FsNode, { kind: "dir" }>) => {
    if (node.dir) node.children = undefined; // fallback snapshots have nothing to re-read
  }, []);

  const flatten = useCallback(
    async (node: Extract<FsNode, { kind: "dir" }>): Promise<Extract<FsNode, { kind: "file" }>[]> => {
      const files: Extract<FsNode, { kind: "file" }>[] = [];
      const walk = async (dir: Extract<FsNode, { kind: "dir" }>) => {
        const children = await readFolder(dir);
        for (const c of children) {
          if (c.kind === "file") {
            if (isMedia(c.name)) files.push(c);
          } else {
            await walk(c);
          }
        }
      };
      await walk(node);
      return files;
    },
    [readFolder],
  );

  const persist = useCallback(
    async (key: string, node: Extract<FsNode, { kind: "dir" }>, galleryName?: string): Promise<void> => {
      if (!node.dir) return; // fallback snapshots have no persistable handle
      try {
        await requestPersistentStorage();
        await saveJobHandle({ jobId: key, handle: node.dir, engineGuid: key, galleryName, createdAt: Date.now() });
      } catch {
        /* best-effort */
      }
    },
    [],
  );

  const savedFolderName = useCallback(async (key: string): Promise<string | null> => {
    try {
      const rec = await getJobHandle(key);
      return rec?.handle?.name ?? null;
    } catch {
      return null;
    }
  }, []);

  const resume = useCallback(async (key: string): Promise<Extract<FsNode, { kind: "dir" }> | null> => {
    try {
      const rec = await getJobHandle(key);
      const h = rec?.handle;
      if (!h) return null;
      // Re-granting read permission is a single click ("allow this site to view files in X").
      if (h.queryPermission) {
        let p = await h.queryPermission({ mode: "read" });
        if (p !== "granted" && h.requestPermission) p = await h.requestPermission({ mode: "read" });
        if (p !== "granted") return null;
      }
      setRootName(h.name);
      return { kind: "dir", name: h.name, path: h.name, dir: h };
    } catch {
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    fallbackRoot.current = null;
    setRootName(null);
  }, []);

  // Memoise the returned interface so its identity is stable across renders (all the
  // methods are already useCallback-stable). Consumers put `fs` in effect deps — a fresh
  // object every render would re-run those effects endlessly (re-reading the folder on a
  // loop → the local list "flashing" repeatedly). Only supported/rootName actually change it.
  return useMemo(
    () => ({ supported, rootName, readFolder, pickDirectory, setFilesFallback, fromDrop, refresh, flatten, persist, savedFolderName, resume, reset }),
    [supported, rootName, readFolder, pickDirectory, setFilesFallback, fromDrop, refresh, flatten, persist, savedFolderName, resume, reset],
  );
};
